import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import Button from 'antd/lib/button'
import Input from 'antd/lib/input'
import { Auth } from 'aws-amplify'
import Divider from 'antd/lib/divider'

import {
    setShowResetPassword,
    setErrorMessage,
    setUser,
    setShowNewPassword,
    showConfirmationForm
} from '../../redux/auth/actions'

const styles = {
    container: {
        paddingTop: '0.64em',
        width: '100%',
        maxWidth: '650px',
        margin: '0 auto'
    },
    inputContainer: {
        marginBottom: '0.6em'
    },
    label: {
        display: 'block',
        marginBottom: '0.2em',
        color: '#666'
    },
    passwordContainer: {
        marginTop: '0.8em',
        marginBottom: '0.6em'
    },
    forgotPassword: {
        color: '#1BAAF1',
        cursor: 'pointer',
        textDecoration: 'none',

    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '1.64em',
        fontWeight: 600
    },
    loginButton: {
        width: 140,
        borderRadius: 7
    },
    error: {
        color: 'red',
        marginBottom: '0.6em',
        textAlign: 'center'
    },
    ssoButton: {
        width: '100%',
        marginBottom: 5,
        background: '#52c41a',
        borderColor: '#52c41a'
    },
};

const Login = ({ showLathamSSO, onStateChange }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const errorMessage = useSelector((state) => state.auth.errorMessage);

    const handleLogin = async (e) => {
        e.preventDefault();

        // Clear any existing error messages
        setErrorMessage('');

        if (!email || email.trim().length === 0) {
            setErrorMessage('Email cannot be empty');
            return;
        }

        if (!password || password.trim().length === 0) {
            setErrorMessage('Password cannot be empty');
            return;
        }

        setIsLoading(true);
        try {
            const cleanEmail = email.toLowerCase().trim();
            const cleanPassword = password.trim();

            const user = await Auth.signIn(cleanEmail, cleanPassword);

            if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
                setUser(user);
                setShowNewPassword(true);
            } else if (onStateChange) {
                onStateChange('signedIn');
            }
        } catch (err) {

            // Handle specific error cases
            switch (err.code) {
                case 'NotAuthorizedException':
                    setErrorMessage('Incorrect email or password.');
                    break;
                case 'UserNotFoundException':
                    setErrorMessage('Incorrect email or password.');
                    break;
                case 'PasswordResetRequiredException':
                    setShowResetPassword(true);
                    break;
                case 'UserNotConfirmedException':
                    showConfirmationForm(email.toLowerCase(), null, true);
                    break;
                case 'InvalidParameterException':
                    setErrorMessage('Please check your email and password format.');
                    break;
                case 'LimitExceededException':
                    setErrorMessage('Too many attempts. Please try again later.');
                    break;
                default:
                    setErrorMessage('An error occurred during login. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e, type) => {
        const value = e.target.value;
        if (type === 'email') {
            setEmail(value.trim());
        } else {
            setPassword(value);
        }
        // Clear error message when user starts typing
        if (errorMessage) {
            setErrorMessage('');
        }
    };

    return (
        <>
            <form onSubmit={handleLogin} style={styles.container}>
                <div style={styles.inputContainer}>
                    <label style={styles.label}>Email:</label>
                    <Input
                        value={email}
                        onChange={(e) => handleInputChange(e, 'email')}
                        placeholder="Email"
                        autoComplete="username"
                        disabled={isLoading}
                        onPaste={(e) => {
                            e.preventDefault();
                            const text = e.clipboardData.getData('text').trim();
                            setEmail(text);
                        }}
                    />
                </div>

                <div style={styles.passwordContainer}>
                    <label style={styles.label}>Password:</label>
                    <Input.Password
                        value={password}
                        onChange={(e) => handleInputChange(e, 'password')}
                        placeholder="Password"
                        autoComplete="current-password"
                        disabled={isLoading}
                        onPaste={(e) => {
                            e.preventDefault();
                            const text = e.clipboardData.getData('text');
                            setPassword(text);
                        }}
                    />
                </div>

                {errorMessage && (
                    <div style={styles.error}>
                        {errorMessage}
                    </div>
                )}

                <div style={styles.buttonContainer}>
                    <span
                        style={styles.forgotPassword}
                        onClick={() => !isLoading && setShowResetPassword(true)}
                    >
                        Forgot Password?
                    </span>

                    <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        style={styles.loginButton}
                        loading={isLoading}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Logging in...' : 'Login'}
                    </Button>
                </div>
            </form>
            {showLathamSSO && (
                <div>
                    <Divider>Or</Divider>
                    <Button
                        htmlType="submit"
                        href={process.env.REACT_APP_LATHAM_SSO_URL}
                        target='_blank'
                        size="large"
                        style={styles.ssoButton}
                        type="primary">
                        Single-Sign On
                    </Button>
                </div>
            )}
        </>
    );
};

export default Login;
